import { Obs, useObserver, useSubtree } from "subtree.js/react";
import { CAPABILITY_LABEL, CAPABILITY_PITCH } from "../../core/primitives/capabilities";
import { formatDate } from "../../core/primitives/format";
import { CapabilityRequestActions, CapabilityRequestState } from "./capability-request-model";

function ContactEmailField() {
  const state = useSubtree(CapabilityRequestState);
  const actions = useSubtree(CapabilityRequestActions);
  const observer = useObserver();
  const value = observer.watch(state.contactEmail);
  const error = observer.watch(state.contactEmailError);
  const id = `capability-contact-${state.capability}`;
  return (
    <div className="text-left">
      <label htmlFor={id} className="mb-1.5 block text-xs font-medium text-white">
        Email for our reply
      </label>
      <input
        id={id}
        type="email"
        autoComplete="email"
        value={value}
        onChange={(e) => actions.setContactEmail(e.target.value)}
        placeholder="you@yourshop.com"
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${id}-error` : undefined}
        className="field"
      />
      {error && (
        <p id={`${id}-error`} className="field-error">
          {error}
        </p>
      )}
    </div>
  );
}

function DeclinedNote() {
  const state = useSubtree(CapabilityRequestState);
  const observer = useObserver();
  const declined = observer.watch(state.declined);
  if (!declined) return null;
  return (
    <div className="note note-danger text-left">
      <p>
        {declined.declinedAt
          ? `Your last request was declined on ${formatDate(declined.declinedAt)}.`
          : "Your last request was declined."}
      </p>
      {declined.reason && <p className="mt-1">{declined.reason}</p>}
    </div>
  );
}

/** The request button, or what stands in for it. Renders nothing once the capability is on or unknown. */
function RequestControl() {
  const state = useSubtree(CapabilityRequestState);
  const actions = useSubtree(CapabilityRequestActions);
  const observer = useObserver();
  const access = observer.watch(state.access);
  const error = observer.watch(state.error);
  const replyTo = observer.watch(state.replyTo);

  switch (access) {
    case "unknown":
    case "on":
      return null;
    case "requested":
      return (
        <p className="text-sm font-medium text-accent">
          {replyTo ? `Requested — we'll be in touch at ${replyTo}.` : "Requested — we'll be in touch."}
        </p>
      );
    case "askOwner":
      return (
        <div className="w-full max-w-sm space-y-2">
          <DeclinedNote />
          <p className="text-sm text-muted">Ask your shop owner or a manager to request it.</p>
        </div>
      );
    case "requestable":
      return (
        <div className="w-full max-w-sm space-y-2">
          <DeclinedNote />
          <ContactEmailField />
          <Obs>
            {(ref) => {
              const request = ref.disableUntilCompleted(actions.request);
              const blocked = ref.watch(state.contactEmailError) !== null;
              const declined = ref.watch(state.declined) !== null;
              return (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={request}
                  disabled={request === undefined || blocked}
                >
                  {declined ? "Ask again" : "Request access"}
                </button>
              );
            }}
          </Obs>
          {error && <p className="note note-danger">{error}</p>}
        </div>
      );
  }
}

/** The whole page a switched-off feature shows in place of itself. */
export function CapabilityRequestPanel() {
  const state = useSubtree(CapabilityRequestState);
  return (
    <div className="mx-auto max-w-lg p-4 pt-12">
      <div className="panel space-y-4 p-6 text-center">
        <p className="eyebrow">{CAPABILITY_LABEL[state.capability]}</p>
        <h1 className="text-lg font-semibold text-white">This feature needs to be switched on</h1>
        <p className="text-sm leading-relaxed text-muted">{CAPABILITY_PITCH[state.capability]}</p>
        <div className="flex justify-center">
          <RequestControl />
        </div>
      </div>
    </div>
  );
}

/** A line under a page whose feature is off, for asking for it without leaving. */
export function CapabilityRequestInline({ prompt }: { prompt: string }) {
  const state = useSubtree(CapabilityRequestState);
  const observer = useObserver();
  const access = observer.watch(state.access);
  if (access === "unknown" || access === "on") return null;
  return (
    <section className="panel space-y-3 p-4 text-center">
      <p className="text-sm font-medium text-white">{prompt}</p>
      <p className="text-[13px] text-muted">{CAPABILITY_PITCH[state.capability]}</p>
      <div className="flex justify-center">
        <RequestControl />
      </div>
    </section>
  );
}
